// RUTA: src/lib/toastError.ts
//
// Wrapper sobre sonner.toast.error que automáticamente agrega acción
// "Crear ticket" al toast. Esto convierte CUALQUIER error transitorio
// del sistema en una oportunidad para que el cliente repore el
// problema con 1 click — sin tener que abrir el panel de soporte y
// llenar un formulario.
//
// Uso típico (1 línea de cambio):
//
//   // antes:
//   toast.error('No se pudo subir la foto');
//
//   // ahora:
//   toastError('No se pudo subir la foto', {
//     section: 'progress-photos-upload',
//     extra: { fileName, fileSize },
//   });
//
// La acción "Crear ticket" usa createTicketAuto que captura todo el
// contexto técnico (URL, role, screenshot, etc.) automáticamente.
// Tras crear, se muestra el modal TicketCreatedDialog mediante un
// evento global para que cualquier parte del sistema lo escuche.

import { toast } from 'sonner';
import { createTicketAuto } from './createTicket';

interface ToastErrorOptions {
  /** Etiqueta corta de la sección/feature donde ocurrió el error.
   *  Va al título del ticket: "[section-label] mensaje". */
  section: string;
  /** Datos extra para el ticket — id de cliente, payload, error real,
   *  cualquier cosa útil para diagnosticar. */
  extra?: Record<string, unknown>;
  /** Si false, NO se muestra el botón "Crear ticket" en el toast.
   *  Útil para errores triviales (ej. validación) donde un ticket
   *  sería ruido. Default true. */
  withTicketAction?: boolean;
  /** Duración del toast en ms (default 8000 — más largo que el
   *  default de sonner para que el usuario alcance a ver la acción) */
  duration?: number;
}

/**
 * Muestra un toast de error con acción "Crear ticket". Si el cliente
 * lo presiona, se crea un ticket de soporte con el contexto técnico
 * y se dispara el evento `iftmx:ticket-created` que el listener
 * global del modal captura para mostrar la confirmación.
 */
export function toastError(message: string, options: ToastErrorOptions) {
  const { section, extra, withTicketAction = true, duration = 8000 } = options;

  toast.error(message, {
    duration,
    action: withTicketAction
      ? {
          label: 'Crear ticket',
          onClick: async () => {
            const result = await createTicketAuto({
              title: `[${section}] ${message.slice(0, 80)}`,
              description: `Error mostrado al usuario:\n\n${message}\n\n**Sección:** ${section}`,
              category: 'bug',
              captureScreenshot: true,
              extra,
            });
            if (result) {
              // Dispatch evento global → TicketCreatedListener mostrará
              // el modal de confirmación. Patrón pub/sub para evitar
              // tener que pasar refs por todo el árbol.
              window.dispatchEvent(
                new CustomEvent('iftmx:ticket-created', {
                  detail: { ticket_number: result.ticket_number },
                }),
              );
            } else {
              toast.error('No se pudo crear el ticket. ¿Tienes sesión activa?');
            }
          },
        }
      : undefined,
  });
}
